import { TextInput as MantineTextInput } from "@mantine/core";
import { componentWrapper } from "../../utilities";
import type {
    CustomTextInputFactory,
    CustomTextInputFormikFactory,
    IProps,
    ITextInputFormikProps,
} from "./text-input.types";
import { useField } from "formik";
import inputStyles from "../input/input.module.scss";
import { Icon } from "../icon";
import { kebabCase } from "lodash";

/**
 * Text input component with optional icon support.
 * @param {IconDefinition} props.icon - Optional icon to display on the left side of the input.
 * @param {boolean} props.activable - Whether the input can be activated.
 * @returns {JSX.Element} The TextInput component.
 */
const TextInput = componentWrapper<CustomTextInputFactory, IProps>(
    "TextInput",
    ({ icon, activable, ...otherProps }, ref) => {
        return (
            <MantineTextInput
                ref={ref}
                data-activable={activable}
                // Had to add this because Mantine internally adds the sm size to the text input label
                labelProps={{ style: { "--input-label-size": undefined } }}
                classNames={{
                    root: inputStyles["wrapperRoot"],
                    label: inputStyles["label"],
                    input: inputStyles["input"],
                    wrapper: inputStyles["inputWrapper"],
                    section: inputStyles["section"],
                }}
                leftSection={icon && <Icon icon={icon} />}
                {...otherProps}
            />
        );
    }
);

/**
 * Text input component integrated with Formik for form handling and validation.
 * @param {string} props.name - The name of the input field for Formik.
 * @param {function} props.validate - Custom validation function for the field.
 * @returns {JSX.Element} The TextInputFormik component.
 */
const TextInputFormik = componentWrapper<CustomTextInputFormikFactory, ITextInputFormikProps>(
    "TextInputFormik",
    ({ name, validate, hideError, ...otherProps }, ref) => {
        const [field, meta] = useField({ name, validate });
        return (
            <TextInput
                ref={ref}
                data-testid={`text-input-${kebabCase(name)}`}
                {...otherProps}
                error={hideError ? undefined : meta.touched ? meta.error : undefined}
                {...field}
            />
        );
    }
);

TextInput.Formik = TextInputFormik;

export { TextInput, TextInputFormik };
